import { Injectable, Injector } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { LocalizedResourcesHelper } from './shared/helpers/LocalizedResourcesHelper';
import { UrlHelper } from './shared/helpers/UrlHelper';
import { MergeObjectsService } from './shared/utils/merge-objects.service';
import {ENVIRONMENT, Environment} from "@shared/utils/environment";

@Injectable({
    providedIn: 'root',
})
export class AppInitializer {
    private readonly environment: Environment;
    constructor(
        private _injector: Injector,
        private _httpClient: HttpClient,
        private _mergeObjectService: MergeObjectsService
    ) {
        this.environment = this._injector.get(ENVIRONMENT);
    }

    init(): () => Promise<boolean> {
        return () => {
            abp.ui.setBusy();
            return new Promise<boolean>((resolve, reject) => {
                const queryStringObj = UrlHelper.getQueryParameters();
                if (queryStringObj.switchAccountToken) {
                    this.linkedAccountAuthenticate(queryStringObj.switchAccountToken, queryStringObj.tenantId, () => {
                        this.getUserConfiguration(() => {
                            this.getUserConfigurationCallback(resolve, reject);
                        });
                    });
                } else {
                    this.getUserConfiguration(() => {
                        this.getUserConfigurationCallback(resolve, reject);
                    });
                }
            });
        };
    }

    private shouldLoadLocale(): boolean {
        return abp.localization.currentLanguage.name && abp.localization.currentLanguage.name !== 'en-US';
    }

    private getCurrentClockProvider(currentProviderName: string): abp.timing.IClockProvider {
        if (currentProviderName === 'unspecifiedClockProvider') {
            return abp.timing.unspecifiedClockProvider;
        }

        if (currentProviderName === 'utcClockProvider') {
            return abp.timing.utcClockProvider;
        }

        return abp.timing.localClockProvider;
    }

    private getUserConfiguration(callback: () => void): void {
        const cookieLangValue = abp.utils.getCookieValue('Abp.Localization.CultureName');
        const token = abp.auth.getToken();

        const requestHeaders = {
            'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
            '.AspNetCore.Culture': `c=${cookieLangValue}|uic=${cookieLangValue}`,
        };

        if (token) {
            requestHeaders['ASO-Authorization'] = `Bearer ${token}`;
        }

        this._httpClient
            .get<any>(`${this.environment.backendUrl}/AbpUserConfiguration/GetAll`, {
                headers: requestHeaders,
            })
            .subscribe((response) => {
                let result = response.result;

                this._mergeObjectService.mergeJSON(abp, result);

                abp.clock.provider = this.getCurrentClockProvider(result.clock.provider);

                moment.locale(abp.localization.currentLanguage.name);

                if (abp.clock.provider.supportsMultipleTimezone) {
                    moment.tz.setDefault(abp.timing.timeZoneInfo.iana.timeZoneId);
                }

                LocalizedResourcesHelper.loadResources(this.environment.frontendUrl, callback);
            });
    }

    private getUserConfigurationCallback(resolve: (value: boolean | PromiseLike<boolean>) => void, reject: (reason?: any) => void): void {
        abp.event.trigger('abp.dynamicScriptsInitialized');
        // do not use constructor injection for AppSessionService
        const appSessionService = this._injector.get(AppSessionService);
        appSessionService.init().then(
            (result) => {
                abp.ui.clearBusy();
                if (this.shouldLoadLocale()) {
                    import(`@angular/common/locales/en`).then((module) => {
                        registerLocaleData(module.default);
                        resolve(result);
                    }, reject);
                } else {
                    resolve(result);
                }
            },
            (err) => {
                abp.ui.clearBusy();
                reject(err);
            }
        );
    }

    private linkedAccountAuthenticate(switchAccountToken: string, tenantId: number, callback: () => void): JQueryPromise<any> {
        abp.multiTenancy.setTenantIdCookie(tenantId);

        return abp
            .ajax({
                url: this.environment.backendUrl + '/api/TokenAuth/LinkedAccountAuthenticate?switchAccountToken=' + switchAccountToken,
                method: 'POST',
                headers: {
                    'Accept-Language': abp.utils.getCookieValue('Abp.Localization.CultureName'),
                    'Abp.TenantId': abp.multiTenancy.getTenantIdCookie(),
                },
            })
            .done((result) => {
                abp.auth.setToken(result.accessToken);
                location.search = '';
                callback();
            });
    }
}
